import React from "react";
import { Alert, Box, Button, Stack } from "@mui/material";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import PageHeader from "../../../components/PageHeader";
import TrainingPlansV2Library from "./training-plans-v2/TrainingPlansV2Library";
import TrainingPlansV2Editor, {
  TRAINING_PLAN_V2_STAGING_LEAVE_MESSAGE,
} from "./training-plans-v2/TrainingPlansV2Editor";
import TrainingPlansV2Assign from "./training-plans-v2/TrainingPlansV2Assign";
import TrainingPlansV2Manage from "./training-plans-v2/TrainingPlansV2Manage";
import TrainingPlansV2CommonTasks from "./training-plans-v2/TrainingPlansV2CommonTasks";
import TrainingPlansV2GuidedTaskBar from "./training-plans-v2/TrainingPlansV2GuidedTaskBar";
import TrainingPlansV2LibraryImportToolbar from "./training-plans-v2/TrainingPlansV2LibraryImportToolbar";
import {
  parseGuidedTask,
  stripGuidedParams,
} from "./training-plans-v2/trainingPlansV2GuidedTaskParams";
import {
  isTrainingPlansV2Enabled,
  trackTrainingPlansV2Event,
} from "./training-plans-v2/services/trainingPlansV2Service";

function TrainingPlansV2IndexRedirect() {
  const { search } = useLocation();
  return <Navigate to={`library${search}`} replace />;
}

const TrainingPlansV2Page = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enabled = isTrainingPlansV2Enabled();
  const activePath = location.pathname;
  const isLibraryRoute = activePath.includes("/training-plans-v2/library");
  const isCommonTasksRoute = activePath.includes("/training-plans-v2/common-tasks");
  const activeSearch = location.search || "";
  const hasGuidedTask = parseGuidedTask(activeSearch) != null;
  const libraryStatus = React.useMemo(() => {
    const raw = new URLSearchParams(activeSearch).get("status");
    const value = raw === "deprecated" ? "archived" : raw;
    return ["all", "active", "draft", "archived", "in_progress"].includes(value) ? value : "all";
  }, [activeSearch]);
  const buildTrainingPlansPath = React.useCallback(
    (path, updates = {}) => {
      const params = new URLSearchParams(activeSearch);
      Object.entries(updates).forEach(([key, value]) => {
        if (!value || (key === "status" && value === "all")) {
          params.delete(key);
          return;
        }
        params.set(key, value);
      });
      const nextSearch = params.toString();
      return `${path}${nextSearch ? `?${nextSearch}` : ""}`;
    },
    [activeSearch],
  );
  const planId = React.useMemo(() => {
    const match = activePath.match(/\/home\/training-plans-v2\/([^/]+)\/(edit|assign|manage)/);
    return match?.[1] || null;
  }, [activePath]);

  const [stagingEditorNavGuard, setStagingEditorNavGuard] = React.useState(false);
  const [libraryImportError, setLibraryImportError] = React.useState("");

  React.useEffect(() => {
    if (!isLibraryRoute) {
      setLibraryImportError("");
    }
  }, [isLibraryRoute]);

  const navigateWithStagingGuard = React.useCallback(
    (to, options) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      const dest = typeof to === "string" ? to : null;
      if (dest == null) {
        navigate(to, options);
        return;
      }
      if (stagingEditorNavGuard && activePath.includes("/edit")) {
        const current = `${location.pathname}${location.search}`;
        if (dest !== current && !window.confirm(TRAINING_PLAN_V2_STAGING_LEAVE_MESSAGE)) {
          return;
        }
      }
      navigate(to, options);
    },
    [stagingEditorNavGuard, activePath, location.pathname, location.search, navigate],
  );

  React.useEffect(() => {
    if (enabled) {
      trackTrainingPlansV2Event("v2_opened", {
        entrypoint: "training_plans_v2",
      });
    }
  }, [enabled]);

  return (
    <>
      <PageHeader
        title="Training Plans"
        subtitle="Unified workspace for plan editing, assignment, and management"
      />
      <Box sx={{ mt: 2, pb: hasGuidedTask ? { xs: 18, sm: 16 } : 0, px: { xs: 2, sm: 3 } }}>
        {!enabled ? (
          <Navigate to="/home/protocols" replace />
        ) : (
          <>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{ borderBottom: "1px solid #E2E8F0", pb: 1 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ flex: "1 1 auto", minWidth: 0 }}
                >
                  <Button
                    variant={activePath.includes("/training-plans-v2/library") ? "contained" : "outlined"}
                    onClick={() =>
                      navigateWithStagingGuard(buildTrainingPlansPath("/home/training-plans-v2/library"))
                    }
                  >
                    Library
                  </Button>
                  <Button
                    variant={isCommonTasksRoute ? "contained" : "outlined"}
                    onClick={() =>
                      navigateWithStagingGuard(
                        `/home/training-plans-v2/common-tasks${stripGuidedParams(location.search)}`,
                      )
                    }
                  >
                    Common tasks
                  </Button>
                  {isLibraryRoute ? (
                    <>
                      <Button
                        variant={libraryStatus === "draft" ? "contained" : "outlined"}
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath("/home/training-plans-v2/library", {
                              status: "draft",
                            }),
                          )
                        }
                      >
                        Work In Progress
                      </Button>
                      <Button
                        variant={libraryStatus === "all" ? "contained" : "outlined"}
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath("/home/training-plans-v2/library", {
                              status: "all",
                            }),
                          )
                        }
                      >
                        All Plans
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath("/home/training-plans-v2/new", { type: "template" }),
                          )
                        }
                      >
                        Create Reusable Plan
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath("/home/training-plans-v2/new", { type: "client" }),
                          )
                        }
                      >
                        Create Client Plan
                      </Button>
                    </>
                  ) : null}
                  {planId ? (
                    <>
                      <Button
                        variant={activePath.includes("/edit") ? "contained" : "outlined"}
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath(`/home/training-plans-v2/${planId}/edit`),
                          )
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant={activePath.includes("/assign") ? "contained" : "outlined"}
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath(`/home/training-plans-v2/${planId}/assign`),
                          )
                        }
                      >
                        Assign
                      </Button>
                      <Button
                        variant={activePath.includes("/manage") ? "contained" : "outlined"}
                        onClick={() =>
                          navigateWithStagingGuard(
                            buildTrainingPlansPath(`/home/training-plans-v2/${planId}/manage`),
                          )
                        }
                      >
                        Manage
                      </Button>
                    </>
                  ) : null}
                </Stack>
                {isLibraryRoute ? (
                  <TrainingPlansV2LibraryImportToolbar onImportError={setLibraryImportError} />
                ) : null}
              </Stack>
              {libraryImportError ? (
                <Alert severity="error" onClose={() => setLibraryImportError("")}>
                  {libraryImportError}
                </Alert>
              ) : null}
            </Stack>
            <Routes>
              <Route
                path="library"
                element={<TrainingPlansV2Library />}
              />
              <Route
                path="common-tasks"
                element={
                  <TrainingPlansV2CommonTasks navigateWithStagingGuard={navigateWithStagingGuard} />
                }
              />
              <Route
                path="new"
                element={
                  <TrainingPlansV2Editor onStagingNavGuardChange={setStagingEditorNavGuard} />
                }
              />
              <Route
                path=":planId/edit"
                element={
                  <TrainingPlansV2Editor onStagingNavGuardChange={setStagingEditorNavGuard} />
                }
              />
              <Route path=":planId/assign" element={<TrainingPlansV2Assign />} />
              <Route path=":planId/manage" element={<TrainingPlansV2Manage />} />
              <Route path="metrics" element={<Navigate to="../library" replace />} />
              <Route path="" element={<TrainingPlansV2IndexRedirect />} />
            </Routes>
            <TrainingPlansV2GuidedTaskBar navigateWithStagingGuard={navigateWithStagingGuard} />
          </>
        )}
      </Box>
    </>
  );
};

export default TrainingPlansV2Page;
